import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { kycService } from '../../services/kycService';
import { KYCSubmission } from '../../types/kyc';
import KYCStatusBadge from './KYCStatusBadge';
import { useThemeMode } from '../../contexts/ThemeContext';

interface KYCStatusCardProps {
  userId: string;
}

const KYCStatusCard: React.FC<KYCStatusCardProps> = ({ userId }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<KYCSubmission | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const data = await kycService.getKYCStatus(userId);
        setSubmission(data);
      } catch (err) {
        console.error('Failed to fetch KYC status:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchStatus();
    }
  }, [userId]);

  if (loading) {
    return (
      <Card
        sx={{
          background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: 3,
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const status = submission?.status || 'not_started';
  const isApproved = status === 'approved';
  const isPending = status === 'pending' || status === 'under_review';
  const isRejected = status === 'rejected';
  const notStarted = !submission;

  const getCardStyle = () => {
    if (isApproved) {
      return {
        borderColor: isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.5)',
        background: isDark
          ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(17, 24, 39, 0.9))'
          : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(255, 255, 255, 0.95))',
      };
    }
    if (isRejected) {
      return {
        borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.5)',
        background: isDark
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(17, 24, 39, 0.9))'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(255, 255, 255, 0.95))',
      };
    }
    if (isPending) {
      return {
        borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.5)',
        background: isDark
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(17, 24, 39, 0.9))'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(255, 255, 255, 0.95))',
      };
    }
    return {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
      background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    };
  };

  const cardStyle = getCardStyle();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        sx={{
          ...cardStyle,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${cardStyle.borderColor}`,
          borderRadius: 3,
          mb: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isApproved
                    ? 'linear-gradient(135deg, #22C55E, #10B981)'
                    : isRejected
                    ? 'linear-gradient(135deg, #EF4444, #DC2626)'
                    : 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                  color: 'white',
                  mr: 2,
                }}
              >
                {isApproved ? <VerifiedIcon /> : isRejected ? <WarningIcon /> : <VerifiedIcon />}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Identity Verification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isApproved
                    ? 'Your identity has been verified'
                    : isPending
                    ? 'Your verification is being reviewed'
                    : isRejected
                    ? 'Your verification was not approved'
                    : 'Complete KYC to access all features'}
                </Typography>
              </Box>
            </Box>
            <KYCStatusBadge status={status === 'not_started' ? 'not_started' : status} />
          </Box>

          {isRejected && submission?.rejection_reason && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">{submission.rejection_reason}</Typography>
            </Alert>
          )}

          {!isApproved && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant={notStarted || isRejected ? 'contained' : 'outlined'}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/kyc')}
                fullWidth
                sx={
                  notStarted || isRejected
                    ? {
                        background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                        },
                      }
                    : {}
                }
              >
                {notStarted
                  ? 'Start Verification'
                  : isRejected
                  ? 'Resubmit Verification'
                  : 'View Status'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default KYCStatusCard;
